import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MFAType } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { LoginPageStep, LoginService } from '../login.service';

@Component({
    selector: 'backup-code',
    styleUrls: ['./login-step.scss'],
    template: `
        <h1 i18n>Enter Backup Code</h1>

        <form class="login-step" [formGroup]="formGroup" (ngSubmit)="submitCode()">
            <p class="body" [style.marginBottom]="'2rem'" i18n>
                To continue, please enter one of your backup codes.
            </p>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'-0.5rem'" i18n>Enter Backup Code</p>
            <lsq-input
                [style.marginBottom]="'3rem'"
                name="backupCode"
                [basicLabel]="false"
                formControlName="backupCode"
                [group]="formGroup"
                [autoFocus]="true"
            >
            </lsq-input>

            <lsq-button
                type="submit"
                size="block"
                [submitting]="isSubmitting"
                [style.marginBottom]="'2rem'"
            >
                Continue
            </lsq-button>

            <ng-container *ngIf="(mfaType$ | async) !== MFAType.TOTP">
                <lsq-button
                    appearance="transparent"
                    size="block"
                    (buttonClick)="handleSendSmsCodeInstead()"
                    [style.marginBottom]="'1.5rem'"
                >
                    Send me an SMS code instead
                </lsq-button>
            </ng-container>

            <ng-container *ngIf="(mfaType$ | async) == MFAType.TOTP">
                <lsq-button
                    appearance="transparent"
                    size="block"
                    (buttonClick)="handleSendTOTPCodeInstead()"
                    [style.marginBottom]="'1.5rem'"
                >
                    Use a Duo Mobile code instead
                </lsq-button>
            </ng-container>

            <lsq-button
                appearance="transparent"
                size="block"
                (buttonClick)="handleTroubleSigningIn()"
            >
                I'm having trouble signing in
            </lsq-button>
        </form>
    `
})
export class BackupCodeComponent {
    public isSubmitting = false;
    private _backupCodeFailure = false;
    public MFAType = MFAType;
    public mfaType: MFAType;
    public mfaType$ = this._loginService.mfaType$;

    private readonly _invalidCodeCopy = this._i18n({
        value: 'Invalid backup code'
    });

    public formGroup = this._formBuilder.group({
        backupCode: [
            '',
            [
                Validators.required,
                () =>
                    this._backupCodeFailure
                        ? {
                              backupCodeInvalid: {
                                  message: this._invalidCodeCopy
                              }
                          }
                        : null
            ]
        ]
    });

    constructor(
        private _i18n: I18n,
        private _formBuilder: FormBuilder,
        private _loginService: LoginService
    ) {}

    public async submitCode(): Promise<void> {
        this._backupCodeFailure = false;

        this.formGroup.markAllAsTouched();
        this.formGroup.get('backupCode').updateValueAndValidity();

        if (this.formGroup.valid) {
            this.isSubmitting = true;
            const backupCode = this.formGroup.get('backupCode').value;

            try {
                await this._loginService.submitBackupCode(backupCode);
            } catch {
                this._backupCodeFailure = true;
                this.formGroup.get('backupCode').updateValueAndValidity();
            }
        }
        this.isSubmitting = false;
    }

    public async handleSendSmsCodeInstead(): Promise<void> {
        await this._loginService.resendMfaCode();
        this._loginService.setLoginPageStep(LoginPageStep.Mfa);
    }

    public async handleSendTOTPCodeInstead(): Promise<void> {
        await this._loginService.handleUseTOTP();
        this._loginService.setLoginPageStep(LoginPageStep.Mfa);
    }

    public async handleTroubleSigningIn(): Promise<void> {
        await this._loginService.showTroubleSigningInModal();
    }
}
